/* eslint-disable react/no-array-index-key */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ThreeDots } from 'react-loader-spinner';
import { isEqual } from 'lodash';
import './css/ProfileOrderModal.css';
import { fillOrderPeople } from '../../../Utils/RestUtils';
import { firstLetterInUpperCase, formatPrice } from '../../../Utils/CommonUtils';
import {
  getEmptyContractInfo,
  getEmptyTourists,
  isContractInfoValid,
  isTouristsValid,
  getContactInfoPayload,
  getTouristsPayload,
  getTouristTitle,
  isOrderPeopleMissing,
  isOrderPeopleEditable,
  getContractInfoFromPerson,
  getTouristsFromOrder,
} from '../../../Utils/OrderUtil';
import {
  BYN_TEXT,
  PROFILE_ORDER_NUMBER,
  PROFILE_ORDER_TOUR,
  PROFILE_ORDER_DEPARTURE_CITY,
  PROFILE_ORDER_OPERATOR,
  PROFILE_ORDER_PROGRAM,
  PROFILE_ORDER_ROOM,
  PROFILE_ORDER_PLACEMENT,
  PROFILE_ORDER_FLIGHT_TO,
  PROFILE_ORDER_FLIGHT_BACK,
  PROFILE_ORDER_PRICE,
  PROFILE_ORDER_TOTAL,
  PROFILE_ORDER_OPERATOR_PRICE,
  PROFILE_ORDER_INSURANCE,
  PROFILE_ORDER_PROMO,
  PROFILE_ORDER_PREPAYMENT,
  PERSON_BUYER,
  PROFILE_ORDER_YES,
  PROFILE_PEOPLE_TITLE,
  PROFILE_PEOPLE_TEXT,
  PROFILE_PEOPLE_FILL,
  PROFILE_PEOPLE_SAVE,
  PROFILE_PEOPLE_SAVE_ERROR,
  PROFILE_PEOPLE_EDIT,
  PROFILE_PEOPLE_CANCEL,
} from '../../../Utils/Constants/GlobalConstants';
import {
  TOURISTS_DATA,
  FLIGHT,
  NOTE,
  BUYER_INFO,
} from '../../../Utils/Constants/CheckoutConstants';
import PriceText from '../../PriceText/PriceText';
import OrderFacts from '../../OrderFacts/OrderFacts';
import OrderTicket from '../../OrderTicket/OrderTicket';
import OrderPerson from '../../OrderPerson/OrderPerson';
import BuyerForm from '../../BuyerForm/BuyerForm';
import TouristForm from '../../TouristForm/TouristForm';
import { ReactComponent as Close } from '../../../images/close-r.svg';

const CONTACT_KEY = 'contact';

function ProfileOrderModal(props) {
  const {
    isVisible,
    modalNode,
    handleModalClick,
    order,
    statusData,
    handleOrderUpdate,
  } = props;
  const [contractInfo, setContractInfo] = useState(getEmptyContractInfo());
  const [tourists, setTourists] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // CONTACT_KEY or a tourist index: saved people are edited one by one
  const [editingKey, setEditingKey] = useState(null);
  const peopleFormNode = useRef(null);
  const isPeopleMissing = isOrderPeopleMissing(order);
  const isPeopleEditable = isOrderPeopleEditable(order);

  // empty forms for an order without people, otherwise the saved data
  useEffect(() => {
    if (!isVisible || !isOrderPeopleEditable(order)) {
      return;
    }

    setEditingKey(null);
    setSaveError('');

    if (isOrderPeopleMissing(order)) {
      setContractInfo(getEmptyContractInfo());
      setTourists(getEmptyTourists(order));
    } else {
      setContractInfo(getContractInfoFromPerson(order.contactInfo));
      setTourists(getTouristsFromOrder(order));
    }
  }, [isVisible, order]);

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const handleEscPress = (e) => {
      if (e.key === 'Escape') {
        handleModalClick();
      }
    };

    document.addEventListener('keydown', handleEscPress, false);

    return () => {
      document.removeEventListener('keydown', handleEscPress, false);
    };
  }, [isVisible, handleModalClick]);

  const renderRow = (label, value) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return null;
    }

    return (
      <div key={`pom-row-${label}`} className="pom_row">
        <div className="pom_label gr_text_14">{label}</div>
        <div className="pom_value br_text_16">
          {Array.isArray(value)
            ? value.map((line, index) => <div key={index}>{line}</div>)
            : value}
        </div>
      </div>
    );
  };

  const renderSection = (title, rows, key = title) => (
    <div key={`pom-section-${key}`} className="pom_section">
      <div className="bm_text_18 mb_12">{title}</div>
      <div className="pom_rows">{rows}</div>
    </div>
  );

  const getInsuranceText = () => {
    if (`${order.flightInsurance}` !== 'true') {
      return '';
    }

    if (order.flightInsuranceValueByn) {
      return `${PROFILE_ORDER_YES}, ${order.flightInsuranceValueByn} ${BYN_TEXT}`;
    }

    return PROFILE_ORDER_YES;
  };

  const getPrepaymentText = () => {
    const split = parseInt(order.split, 10);

    if (!split || split === 100 || !order.splitPaymentValue) {
      return '';
    }

    return `${split}%, ${formatPrice(order.splitPaymentValue)} ${BYN_TEXT}`;
  };

  const renderTour = () =>
    renderSection(PROFILE_ORDER_TOUR, [
      renderRow(PROFILE_ORDER_DEPARTURE_CITY, order.departureCity),
      renderRow(PROFILE_ORDER_ROOM, firstLetterInUpperCase(order.roomType)),
      renderRow(PROFILE_ORDER_PLACEMENT, order.settlementType),
      renderRow(PROFILE_ORDER_OPERATOR, order.operator),
      renderRow(PROFILE_ORDER_PROGRAM, order.program),
    ]);

  const renderSpinner = () => (
    <ThreeDots
      visible
      height="24"
      width="30"
      color="rgba(0, 0, 0, .5)"
      radius="5"
      ariaLabel="three-dots-loading"
    />
  );

  const renderFlight = () => {
    const hasFlight =
      (order.toTrip && order.toTrip.length > 0) ||
      (order.backTrip && order.backTrip.length > 0);

    if (!hasFlight) {
      return null;
    }

    return (
      <div className="pom_section">
        <div className="bm_text_18 mb_12">{FLIGHT}</div>
        <div className="pom_tickets">
          {order.toTrip && order.toTrip.length > 0 && (
            <OrderTicket title={PROFILE_ORDER_FLIGHT_TO} segments={order.toTrip} />
          )}
          {order.backTrip && order.backTrip.length > 0 && (
            <OrderTicket
              title={PROFILE_ORDER_FLIGHT_BACK}
              segments={order.backTrip}
            />
          )}
        </div>
      </div>
    );
  };

  const renderPrice = () => (
    <div className="pom_section">
      <div className="pom_total">
        <div className="bm_text_18">{PROFILE_ORDER_PRICE}</div>
        {order.totalPriceByn && (
          <div className="pom_total_value">
            <div className="gr_text_13">{PROFILE_ORDER_TOTAL}</div>
            <div className="bb_text_22">
              <PriceText
                text={`${formatPrice(order.totalPriceByn)} ${BYN_TEXT}`}
              />
            </div>
          </div>
        )}
      </div>
      <div className="pom_rows">
        {renderRow(
          PROFILE_ORDER_OPERATOR_PRICE,
          order.originalPrice && (
            <PriceText text={`${order.originalPrice} ${order.currency}`} />
          ),
        )}
        {renderRow(PROFILE_ORDER_PREPAYMENT, getPrepaymentText())}
        {renderRow(PROFILE_ORDER_INSURANCE, getInsuranceText())}
        {renderRow(PROFILE_ORDER_PROMO, order.promoCode)}
      </div>
    </div>
  );

  // the same handlers as on checkout: a changed field becomes valid
  const changeContract = (name, value) => {
    setContractInfo((prev) => ({
      ...prev,
      [name]: value,
      [`${name}Valid`]: true,
    }));
  };

  const changeContractValid = (name, value) => {
    setContractInfo((prev) => ({ ...prev, [`${name}Valid`]: value }));
  };

  const changeContractSex = (value) => {
    setContractInfo((prev) => ({ ...prev, sex: value, sexValid: true }));
  };

  // Input gives the tourist id as a number or as an element id string
  const updateTourist = (id, changes) => {
    setTourists((prev) =>
      prev.map((tourist) =>
        (`${tourist.id}` === `${id}` ? { ...tourist, ...changes } : tourist)));
  };

  const changeTourist = (name, value, id) => {
    updateTourist(id, { [name]: value, [`${name}Valid`]: true });
  };

  const changeTouristValid = (name, value, id) => {
    updateTourist(id, { [`${name}Valid`]: value });
  };

  const changeTouristSex = (value, id) => {
    updateTourist(id, { sex: value, sexValid: true });
  };

  // drops the unsaved changes and opens the person form, null closes it
  const startPersonEditing = (key) => {
    setContractInfo(getContractInfoFromPerson(order.contactInfo));
    setTourists(getTouristsFromOrder(order));
    setSaveError('');
    setEditingKey(key);
  };

  const cancelPeopleEditing = () => {
    startPersonEditing(null);
  };

  const sendPeople = (payload) => {
    const token = window.localStorage.getItem('accessToken');

    setIsSaving(true);
    setSaveError('');

    fillOrderPeople(order.id, payload, token)
      .then((response) => {
        if (response && response.data && response.data.id) {
          handleOrderUpdate(response.data);
        } else {
          setSaveError(PROFILE_PEOPLE_SAVE_ERROR);
        }
      })
      .catch(() => {
        setSaveError(PROFILE_PEOPLE_SAVE_ERROR);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  // the first filling: the buyer and all tourists at once
  const savePeople = () => {
    sendPeople({
      contactInfo: getContactInfoPayload(contractInfo),
      tourists: getTouristsPayload(tourists),
    });
  };

  // one edited person, the others are sent as they are saved
  const savePerson = () => {
    const savedTourists = order.tourists || [];

    if (editingKey === CONTACT_KEY) {
      sendPeople({
        contactInfo: getContactInfoPayload(contractInfo),
        tourists: savedTourists,
      });
      return;
    }

    const [editedTourist] = getTouristsPayload([tourists[editingKey]]);

    sendPeople({
      contactInfo: order.contactInfo,
      tourists: savedTourists.map((tourist, index) =>
        (index === editingKey ? editedTourist : tourist)),
    });
  };

  // the payloads are compared: the form state also keeps the valid flags
  const isPersonChanged = () => {
    if (editingKey === CONTACT_KEY) {
      return !isEqual(
        getContactInfoPayload(contractInfo),
        getContactInfoPayload(getContractInfoFromPerson(order.contactInfo)),
      );
    }

    return !isEqual(
      getTouristsPayload([tourists[editingKey]]),
      getTouristsPayload([getTouristsFromOrder(order)[editingKey]]),
    );
  };

  const renderPeopleBanner = () => (
    <div className="pom_people_banner">
      <div>
        <div className="bm_text_16 mb_5">{PROFILE_PEOPLE_TITLE}</div>
        <div className="gr_text_14">{PROFILE_PEOPLE_TEXT}</div>
      </div>
      <button
        type="button"
        className="pom_btn btn__yellow bm_text_16"
        onClick={() =>
          peopleFormNode.current.scrollIntoView({ behavior: 'smooth' })}
      >
        {PROFILE_PEOPLE_FILL}
      </button>
    </div>
  );

  const renderSaveButtons = (isValid, handleSave, hasCancel) => (
    <div className="pom_people_save">
      {saveError && <div className="rr_text_14">{saveError}</div>}
      <div className="pom_people_buttons">
        {hasCancel && (
          <button
            type="button"
            className="pom_btn light_gray_button bm_text_16"
            disabled={isSaving}
            onClick={cancelPeopleEditing}
          >
            {PROFILE_PEOPLE_CANCEL}
          </button>
        )}
        <button
          type="button"
          className="pom_btn btn__yellow bm_text_16"
          disabled={!isValid || isSaving}
          onClick={handleSave}
        >
          {isSaving ? renderSpinner() : PROFILE_PEOPLE_SAVE}
        </button>
      </div>
    </div>
  );

  const renderPeopleForm = () => (
    <div ref={peopleFormNode} className="pom_people_form">
      <div className="bm_text_20 pom_group_title">{PROFILE_PEOPLE_TITLE}</div>
      <div className="ch_section_w">
        <div className="bb_text_20 mb_20">{BUYER_INFO}</div>
        <BuyerForm
          contractInfo={contractInfo}
          handleChange={changeContract}
          handleValid={changeContractValid}
          handleSexChange={changeContractSex}
        />
      </div>
      {tourists.map((tourist, index) => (
        <TouristForm
          key={`pom-tourist-form-${tourist.id}`}
          title={getTouristTitle(order, index)}
          tourist={tourist}
          handleChange={changeTourist}
          handleValid={changeTouristValid}
          handleSexChange={changeTouristSex}
        />
      ))}
      {renderSaveButtons(
        isContractInfoValid(contractInfo) && isTouristsValid(tourists),
        savePeople,
        false,
      )}
    </div>
  );

  const renderEditButton = (key) =>
    isPeopleEditable && (
      <button
        type="button"
        className="pom_btn pom_btn_small light_gray_button bm_text_14"
        disabled={isSaving || editingKey === key}
        onClick={() => startPersonEditing(key)}
      >
        {PROFILE_PEOPLE_EDIT}
      </button>
    );

  const renderBuyer = () => {
    if (editingKey === CONTACT_KEY) {
      return (
        <div className="ch_section_w pom_people_form">
          <div className="bb_text_20 mb_20">{BUYER_INFO}</div>
          <BuyerForm
            contractInfo={contractInfo}
            handleChange={changeContract}
            handleValid={changeContractValid}
            handleSexChange={changeContractSex}
          />
          {renderSaveButtons(
            isContractInfoValid(contractInfo) && isPersonChanged(),
            savePerson,
            true,
          )}
        </div>
      );
    }

    return (
      <OrderPerson
        title={PERSON_BUYER}
        person={order.contactInfo || {}}
        email={order.email || ''}
        phone={order.phoneNumber || ''}
        action={renderEditButton(CONTACT_KEY) || null}
      />
    );
  };

  const renderTouristCard = (tourist, index) => {
    if (editingKey === index && tourists[index]) {
      return (
        <div key={`pom-tourist-${index}`} className="pom_people_form">
          <TouristForm
            title={getTouristTitle(order, index)}
            tourist={tourists[index]}
            handleChange={changeTourist}
            handleValid={changeTouristValid}
            handleSexChange={changeTouristSex}
          >
            {renderSaveButtons(
              isTouristsValid([tourists[index]]) && isPersonChanged(),
              savePerson,
              true,
            )}
          </TouristForm>
        </div>
      );
    }

    return (
      <OrderPerson
        key={`pom-tourist-${index}`}
        title={getTouristTitle(order, index)}
        person={tourist}
        action={renderEditButton(index) || null}
      />
    );
  };

  const renderPeople = () => (
    <>
      {renderBuyer()}
      {order.tourists && order.tourists.length > 0 && (
        <div className="bm_text_20 pom_group_title">{TOURISTS_DATA}</div>
      )}
      {order.tourists && order.tourists.map(renderTouristCard)}
    </>
  );

  const renderBody = () => (
    <div className="pom_body custom_scroll_container">
      {statusData.hint && (
        <div className="gr_text_14 pom_hint">{statusData.hint}</div>
      )}
      {isPeopleMissing && renderPeopleBanner()}
      <OrderFacts order={order} />
      {renderTour()}
      {renderFlight()}
      {renderPrice()}
      {isPeopleMissing ? renderPeopleForm() : renderPeople()}
      {order.note && renderSection(NOTE, [renderRow(NOTE, order.note)])}
    </div>
  );

  return (
    <div className={`pom_container ${isVisible ? 'pom_open' : ''}`}>
      <div
        className="pom_modal"
        ref={(node) => {
          modalNode.current = node;
        }}
      >
        {order && (
          <div className="pom_title_line">
            <div className="pom_title_w">
              <div className="pom_title_top">
                <div className="gr_text_14">
                  {`${PROFILE_ORDER_NUMBER}${order.externalId}`}
                </div>
                {statusData.text && (
                  <div className={`pr_status ${statusData.className}`}>
                    {statusData.text}
                  </div>
                )}
              </div>
              <div className="pom_title">
                {firstLetterInUpperCase(order.hotelName)}
                {order.hotelCategory && (
                  <span className="pom_category">
                    {` ${order.hotelCategory}*`}
                  </span>
                )}
              </div>
              <div className="gr_text_14">
                {`${order.resort}, ${order.countryTo}`}
              </div>
            </div>
            <button
              type="button"
              className="pom_close_but"
              onClick={handleModalClick}
            >
              <Close />
            </button>
          </div>
        )}

        {/* remount on open to reset scroll */}
        {isVisible && order && renderBody()}
      </div>
    </div>
  );
}

ProfileOrderModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  modalNode: PropTypes.instanceOf(Object).isRequired,
  handleModalClick: PropTypes.func.isRequired,
  order: PropTypes.instanceOf(Object),
  statusData: PropTypes.instanceOf(Object).isRequired,
  handleOrderUpdate: PropTypes.func.isRequired,
};

ProfileOrderModal.defaultProps = {
  order: null,
};

export default ProfileOrderModal;
